// Custom input component for Photon with on optional label above and a helper text below (which may be an error message)
import React, { InputHTMLAttributes, useMemo, useState } from 'react'
import styles from './PhotonInput.module.css'
import classNames from 'classnames'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { CircleInfoFillIcon } from '../Icons/CircleInfoFillIcon'
import { CaptionMD } from '../Text'

interface PhotonInputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
  containerClassName?: string
  error?: boolean
  helperText?: string
  label?: string
  labelClassName?: string
  theme?: 'light' | 'dark'
}

export const PhotonInput: React.FC<PhotonInputProps> = ({
  className,
  containerClassName,
  error,
  helperText,
  label,
  labelClassName,
  theme = 'light',
  ...props
}) => {
  const [touched, setTouched] = useState(false)
  const [isActive, setActive] = useState(false)

  const isValidName = !touched || !error

  const helperTextColor = useMemo(() => {
    if (!isValidName) return theme === 'light' ? 'error-600' : 'error-500'
    return 'core-gray-600'
  }, [isValidName, theme])

  const borderColor = useMemo(() => {
    if (!isValidName) return theme === 'light' ? 'border-error-600' : 'border-error-500'
    if (isActive) return theme === 'light' ? 'border-core-gray-950' : 'border-core-gray-400'

    return theme === 'light' ? 'border-core-gray-300' : 'border-core-gray-800'
  }, [isActive, isValidName, theme])

  return (
    <label
      className={classNames(containerClassName, {
        'opacity-40': props.disabled,
      })}
    >
      {label && (
        <CaptionMD
          color={theme === 'light' ? 'core-gray-700' : 'core-gray-400'}
          className={classNames('mb-1', labelClassName)}
        >
          {label}
        </CaptionMD>
      )}

      <div className={classNames('relative', styles.inputContainer)}>
        <input
          {...props}
          data-testid={label}
          id={props.name}
          onBlur={(e) => {
            setActive(false)
            setTouched(true)
            props.onBlur?.(e)
          }}
          onFocus={() => setActive(true)}
          className={classNames(
            'w-full border-[1.5px] rounded-[10px] p-4 photon-caption-md',
            'focus-visible:outline-none focus-within:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0',
            borderColor,
            {
              'bg-core-gray-900 text-white': theme === 'dark',
              'bg-white text-core-gray-950': theme === 'light',
            },
            className,
          )}
        />

        {props.type === 'date' && (
          <CalendarIcon
            color={theme === 'light' ? 'core-gray-500' : 'core-gray-400'}
            className={'absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2'}
          />
        )}
      </div>

      {helperText && (
        <CaptionMD color={helperTextColor} className="mt-1 flex gap-1">
          {error && <CircleInfoFillIcon color="current" />}
          {helperText}
        </CaptionMD>
      )}
    </label>
  )
}
